import SearchableSelect from '../../components/ui/SearchableSelect';
import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import POReceiveModal from './POReceiveModal';
import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { Package, Truck, CheckCircle, Clock, Search, Eye } from "lucide-react";

export default function POTracking() {
  const [tracking, setTracking] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");

  const [statusFilter, setStatusFilter] = useState("All");

  const [selectedPO, setSelectedPO] = useState(null);

  const [details, setDetails] = useState(null);

  const loadTracking = async () => {
    try {
      const res = await fetch("/api/tracking/list", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setTracking(data.data);
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTracking();
  }, []);

  const openDetails = async (id) => {
    const res = await fetch(
      `/api/tracking/${id}`,

      {
        credentials: "include",
      },
    );

    const data = await res.json();

    if (data.status === "success") {
      setDetails(data.data);

      setSelectedPO(id);
    } else {
      alert(data.message);
    }
  };

  const filtered = useMemo(() => {
    return tracking.filter((row) => {
      const matchSearch =
        row.po_number.toLowerCase().includes(search.toLowerCase()) ||
        (row.vendor||'').toLowerCase().includes(search.toLowerCase()) ||
        (row.branch||'').toLowerCase().includes(search.toLowerCase());

      const matchStatus = statusFilter === "All" || row.stage === statusFilter;

      return matchSearch && matchStatus;
    });
  }, [tracking, search, statusFilter]);

  const pendingDispatch = tracking.filter(
    (x) => x.stage === "Pending Dispatch",
  ).length;

  const awaitingReceipt = tracking.filter(
    (x) => x.stage === "Awaiting Receipt",
  ).length;

  const completed = tracking.filter((x) => x.stage === "Received").length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-10">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* ================= HEADER ================= */}

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-serif">Purchase Order Tracking</h1>

            <p className="text-sm text-gray-400">
              Monitor deliveries from finalized purchase orders.
            </p>
          </div>
        </div>

        {/* ================= KPIs ================= */}

        <div className="grid md:grid-cols-3 gap-6">
          <StatCard
            title="Pending Dispatch"
            value={pendingDispatch}
            icon={<Clock size={22} />}
            color="amber"
          />

          <StatCard
            title="Awaiting Receipt"
            value={awaitingReceipt}
            icon={<Truck size={22} />}
            color="blue"
          />

          <StatCard
            title="Completed"
            value={completed}
            icon={<CheckCircle size={22} />}
            color="emerald"
          />
        </div>

        {/* ================= FILTERS ================= */}

        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-3 text-gray-400" />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PO / Vendor / Branch"
              className="w-full pl-10 pr-4 py-2 rounded-xl bg-white/5 border border-white/10"
            />
          </div>

          <SearchableSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl bg-white/5 border border-white/10 px-4"
          >
            <option>All</option>

            <option>Pending Dispatch</option>

            <option>Awaiting Receipt</option>

            <option>Received</option>
            <option>Partially Received</option>
          </SearchableSelect>
        </div>

        {/* ================= TABLE ================= */}

        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-left">
              <tr>
                <th className="px-6 py-4 text-left">PO</th>

                <th>Vendor</th>

                <th>Branch</th>

                <th>Delivery</th>

                <th>Items</th>

                <th>Status</th>

                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-white/10 hover:bg-white/5"
                >
                  <td className="px-6 py-4">{row.po_number}<div className="mt-1 text-xs text-gray-400">{row.po_type==='direct'?'Direct PO':'Quotation PO'}</div></td>

                  <td>{row.vendor}</td>

                  <td>{row.branch}</td>

                  <td>
                    {row.delivery_date
                      ? new Date(row.delivery_date).toLocaleDateString()
                      : "-"}
                  </td>

                  <td>{row.items}</td>

                  <td>
                    <StatusBadge status={row.stage} />
                  </td>

                  <td><TableActions>
                    <PermissionButton permissionModule="purchase_orders"
                      onClick={() => openDetails(row.id)}
                      className="px-3 py-2 rounded-lg border border-white/10 hover:bg-white/10"
                    >
                      <Eye size={16} />
                    </PermissionButton>
                  </TableActions></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Part 2 will render the modal here */}

      {selectedPO && details && (
        <TrackingModal
          details={details}
          onClose={() => {
            setSelectedPO(null);
            setDetails(null);
          }}
          refresh={loadTracking}
        />
      )}
    </DashboardLayout>
  );
}

function TrackingModal({ details, onClose, refresh }) {
  const [receiving,setReceiving]=useState(false);

  return (
    <Modal onClose={onClose}>
      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-serif">{details.po_number}</h2>

          <p className="text-sm text-gray-400">Purchase Order Tracking</p>
        </div>

        <div className="grid grid-cols-2 gap-6 text-sm">
          <Info title="Vendor" value={details.vendor} />

          <Info title="Branch" value={details.branch} />

          <Info
            title="Delivery Date"
            value={
              details.delivery_date
                ? new Date(details.delivery_date).toLocaleDateString()
                : "-"
            }
          />

          <Info title="Status" value={details.delivery_status} />

          <Info
            title="Delivered At"
            value={
              details.delivered_at
                ? new Date(details.delivered_at).toLocaleString()
                : "-"
            }
          />

          <Info
            title="Received At"
            value={
              details.received_at
                ? new Date(details.received_at).toLocaleString()
                : "-"
            }
          />
        </div>

        <Timeline details={details} />

        <div>
          <h3 className="font-medium mb-3">Items</h3>

          <div className="rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-left">
                <tr>
                  <th className="px-4 py-3 text-left">Item</th>

                  <th>Qty</th>

                  <th>Unit</th>
                </tr>
              </thead>

              <tbody>
                {details.items.map((item, i) => (
                  <tr key={i} className="border-t border-white/10">
                    <td className="px-4 py-3">{item.item}</td>

                    <td>{item.qty}</td>

                    <td>{item.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {details.notes && (
          <div>
            <h3 className="font-medium mb-2">Notes</h3>

            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              {details.notes}
            </div>
          </div>
        )}

        {details.terms && (
          <div>
            <h3 className="font-medium mb-2">Terms</h3>

            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              {details.terms}
            </div>
          </div>
        )}

        {!!details.receipts?.length&&<section className="space-y-3"><h3 className="font-medium">Receipt history</h3>{details.receipts.map(r=><div key={r.id} className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm"><div className="flex flex-wrap justify-between gap-2"><strong>GRN-{r.id} · {r.invoice_no}</strong><span className="text-amber-400">₹{Number(r.total_amount).toFixed(2)}</span></div><p className="mt-1 text-xs text-gray-400">{r.actor_name} · {new Date(r.created_at).toLocaleString()} · Invoice {r.invoice_date}</p>{r.items.map((i,n)=><div key={n} className="mt-2 border-t border-white/10 pt-2"><p>{i.item_name}: received {i.received_qty}, accepted {i.accepted_qty}, rejected {i.rejected_qty} {i.unit} · {i.quality}</p>{i.rejection_reason&&<p className="text-red-300">Reason: {i.rejection_reason}</p>}{i.notes&&<p className="text-gray-400">{i.notes}</p>}<p className="text-xs text-gray-500">Batch {i.batch_no||'—'} · Expiry {i.expiry_date||'—'}</p></div>)}{r.notes&&<p className="mt-2 text-gray-400">{r.notes}</p>}<div className="mt-2 flex flex-wrap gap-3">{r.attachments?.map(a=><a key={a.id} href={`/api/tracking/${details.id}/attachments/${a.id}`} target="_blank" rel="noreferrer" className="text-amber-400 underline">{a.name}</a>)}</div></div>)}</section>}

        <div className="flex justify-end gap-3">
          <PermissionButton permissionModule="purchase_orders"
            onClick={onClose}
            className="px-5 py-2 rounded-xl border border-white/10"
          >
            Close
          </PermissionButton>

          {details.po_status==='Approved' && (details.po_type==='direct'||['Delivered','Partially Received'].includes(details.delivery_status)) && !details.received_at && (
            <PermissionButton permissionModule="purchase_orders" permissionAction="receive"
              onClick={()=>setReceiving(true)}
              className="px-6 py-2 rounded-xl bg-emerald-500 text-white"
            >
              Receive Goods
            </PermissionButton>
          )}
        </div>
      </div>
      {receiving&&<POReceiveModal details={details} onClose={()=>setReceiving(false)} onSaved={async()=>{await refresh();onClose();}}/>}
    </Modal>
  );
}

function Timeline({ details }) {
  const delivered = details.delivered_at != null;

  const received = details.received_at != null;

  return (
    <div>
      <h3 className="font-medium mb-4">Delivery Timeline</h3>

      <div className="flex justify-between">
        <Step done label="Vendor Finalized" />

        <Step done={delivered} label="Delivered" />

        <Step done={received} label="Received" />
      </div>
    </div>
  );
}

function Step({ done, label }) {
  return (
    <div className="flex flex-col items-center flex-1">
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
          done ? "bg-emerald-500 text-white" : "bg-white/10"
        }`}
      >
        ✓
      </div>

      <p className="mt-2 text-xs">{label}</p>
    </div>
  );
}

function Info({ title, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{title}</p>

      <p className="mt-1">{value || "-"}</p>
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="w-full max-w-5xl rounded-2xl bg-[#0b0b0f] border border-white/10 p-8 relative max-h-[90vh] overflow-y-auto">
        <PermissionButton permissionModule="purchase_orders"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-white"
        >
          ✕
        </PermissionButton>

        {children}
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  const colors = {
    amber: "text-amber-400 bg-amber-400/10",
    blue: "text-blue-400 bg-blue-400/10",
    emerald: "text-emerald-400 bg-emerald-400/10",
    red: "text-red-400 bg-red-400/10",
  };

  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-400">{title}</p>

          <h2 className="text-3xl font-bold mt-2">{value}</h2>
        </div>

        <div
          className={`w-14 h-14 rounded-xl flex items-center justify-center ${colors[color]}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  switch (status) {
    case "Pending Dispatch":
      return (
        <span className="px-3 py-1 rounded-full text-xs bg-amber-400/20 text-amber-400">
          Pending Dispatch
        </span>
      );

    case "Awaiting Receipt":
      return (
        <span className="px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
          Awaiting Receipt
        </span>
      );

    case "Received":
      return (
        <span className="px-3 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
          Received
        </span>
      );

    default:
      return (
        <span className="px-3 py-1 rounded-full text-xs bg-gray-500/20 text-gray-300">
          {status}
        </span>
      );
  }
}
